// Справка: горячие клавиши (из реестра команд - всегда актуальны), интерактивная памятка по СТО с образцами
// и кнопками «Сделать», знакомство при первом запуске (неназойливая карточка, не окно).
import { useEffect, useMemo, useState } from 'react';
import type { RegistryApi, DialogProps } from '../../core/registry';
import { registry } from '../../core/registry';
import { runCommand } from '../../core/ctx';
import { appStore, readPref, writePref, useApp } from '../../core/store';
import { Btn, Kbd, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { openPalette } from './palette';
import { USE_NBSP } from '../../core/schema/constants';

// ------------------------------------------------------------------------------------------------ клавиши
const GROUPS: [string, (id: string) => boolean][] = [
  ['Файл и проверка', (id) => id.startsWith('file.') || id.startsWith('sto.') || id.startsWith('project.')],
  ['Правка', (id) => id.startsWith('edit.') || id.startsWith('block.move') || id.startsWith('block.dup') || id.startsWith('block.del')],
  ['Стили и оформление', (id) => id.startsWith('format.') || id.startsWith('block.') || id.startsWith('list.')],
  ['Вставка', (id) => id.startsWith('insert.') || id.startsWith('math.') || id.startsWith('refs.') || id.startsWith('table.') || id.startsWith('figure.')],
  ['Вид и навигация', () => true],
];
const FIXED: [string, string][] = [
  ['Enter', 'Новый абзац (отступ 1,25 см и выравнивание по ширине - сами)'],
  ['Shift+Enter', 'Новая строка в том же абзаце (разрыв строки, как в Word)'],
  // неразрывные пробел и дефис - только при USE_NBSP (иначе сочетания дают обычные знаки - в DOCX их нет)
  ...(USE_NBSP ? [['Ctrl+Shift+Пробел', 'Неразрывный пробел'], ['Ctrl+Shift+-', 'Неразрывный дефис']] as [string, string][] : []),
  ['Ctrl+= / Ctrl+Shift+=', 'Нижний / верхний индекс (как в Word; в тексте - вместо масштаба)'],
  ['Ctrl+Z сразу после «- »', 'Отменить автоформат - оставить набранные знаки'],
  ['Ctrl+← / Ctrl+→', 'На слово влево / вправо (с Shift - выделить); Ctrl+Backspace / Ctrl+Delete - удалить слово'],
  ['Tab / Shift+Tab', 'В перечислении: уровень ниже / выше; в таблице - следующая / предыдущая ячейка'],
  ['Enter на пустой позиции', 'Закончить перечисление; Backspace в начале позиции - снять маркер'],
  ['/', 'В начале абзаца или после пробела: меню вставки с поиском («/рис», «/табл»)'],
  ['# + пробел', 'В начале абзаца: раздел (## - подраздел, ### - пункт)'],
  ['- + пробел', 'В начале абзаца: перечисление с дефисом («а) » - буквы, «1) » - цифры)'],
  ['Ctrl+V', 'Вставка из Word, Excel, браузера; картинки и скриншоты - сразу рисунком с подписью'],
  ['Ctrl+Shift+V', 'Вставить только текст (без оформления)'],
  ['F6', 'Переход: лента → документ → панели → строка состояния'],
  ['Ctrl+колесо мыши', 'Масштаб листа'],
  ['Esc', 'Закрыть окно, поиск, меню; выйти из режима «Фокус»'],
];

export function ShortcutsDialog({ close }: DialogProps) {
  const [q, setQ] = useState('');
  const groups = useMemo(() => {
    const used = new Set<string>();
    const cmds = registry.commands.list().filter((c) => c.shortcut);
    return GROUPS.map(([title, f]) => {
      const items = cmds.filter((c) => !used.has(c.id) && f(c.id));
      items.forEach((c) => used.add(c.id));
      return { title, items };
    }).filter((g) => g.items.length);
  }, []);
  const m = (s: string) => !q || s.toLowerCase().includes(q.toLowerCase());
  return (
    <Modal title="Горячие клавиши" onClose={() => close()} width={860} testid="shortcuts-dialog">
      <input className="search search-wide" placeholder="Найти действие или клавишу…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="keys-grid">
        {groups.map((g) => {
          const items = g.items.filter((c) => m(c.title) || m(c.shortcut || ''));
          if (!items.length) return null;
          return (
            <section key={g.title} className="keys-group">
              <h3>{g.title}</h3>
              {items.map((c) => (
                <div key={c.id} className="keys-row" onClick={() => { close(); runCommand(c.id); }} title="Выполнить">
                  <span className="keys-ico"><Icon name={c.icon} size={14} /></span>
                  <span className="keys-title">{c.title}</span>
                  <Kbd keys={c.shortcut!} />
                </div>
              ))}
            </section>
          );
        })}
        <section className="keys-group">
          <h3>В тексте</h3>
          {FIXED.filter(([k, d]) => m(k) || m(d)).map(([k, d]) => (
            <div key={k} className="keys-row"><span className="keys-title">{d}</span><span className="kbd-group"><kbd>{k}</kbd></span></div>
          ))}
        </section>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ памятка по СТО
interface GuideTopic { id: string; title: string; icon: string; rules: string[]; sample?: React.ReactNode; action?: [string, string][] }

const TOPICS: GuideTopic[] = [
  { id: 'write', title: 'Как писать', icon: 'keyboard', rules: [
    'Пишите как в Word в режиме «Разметка страницы»: что видно на листе, то и будет в DOCX и PDF - те же отступы, переносы строк и страниц.',
    'Enter - новый абзац: абзацный отступ 1,25 см, выравнивание по ширине и интервал 1,5 ставятся сами. Shift+Enter - новая строка в том же абзаце.',
    'Заголовки: Ctrl+Alt+1 / 2 / 3 (или «# », «## », «### » в начале строки) - номер ставится сам; Ctrl+Alt+0 - обычный текст.',
    'Перечисление: «- » в начале строки (или «а) » - с буквами). Маркер встаёт с абзацного отступа, перенос строки позиции - к левому полю. Tab / Shift+Tab - уровень, Enter на пустой позиции - закончить. Ctrl+Z сразу после «- » - оставить знаки как набраны.',
    USE_NBSP ? 'Ctrl+Shift+Пробел - неразрывный пробел («рисунке 3», «5 кг»), Ctrl+Shift+- - неразрывный дефис, Ctrl+Enter - новая страница.'
      : 'Пробелы - обычные («рисунке 3», «5 кг», «№ 5»): неразрывных в отчёте нет (Word показывал бы их кружками «°»). Ctrl+Enter - новая страница.',
    'Ctrl+B / I / U - полужирный, курсив, подчёркнутый; Ctrl+= / Ctrl+Shift+= - нижний / верхний индекс.',
    'Строка состояния внизу показывает, что вы сейчас пишете: «Абзац», «Перечисление «-», уровень 1», «Подпись рисунка»… (наведите - параметры оформления).',
    'Ctrl+Shift+8 - непечатаемые знаки: ¶, пробелы, неразрывные пробелы °, разрывы строк ↵.',
    'Щелчок в поле листа слева или справа от строки - курсор в начало или конец этой строки, Shift+щелчок - выделение до неё. Набор при выделенном рисунке - новый абзац после рисунка.',
    'Вид → «Как при печати» - лист без подсказок, подчёркиваний проверки и заполнителей: ровно то, что будет в PDF.',
  ], sample: <>
    <p className="gs-p">Для исследования системы использованы следующие методы:</p>
    <p className="gs-p gs-li">- частотный анализ устойчивости замкнутой системы по критерию Найквиста с учётом запаздывания;</p>
    <p className="gs-p gs-li">- моделирование переходных процессов.</p>
    <p className="gs-p">Варианты заданий приведены в таблице 2.</p>
  </> },
  { id: 'page', title: 'Страница и шрифт', icon: 'file', rules: [
    'А4, поля: левое 25 мм, правое 15 мм, верхнее и нижнее 20 мм (4.1.3).',
    'Times New Roman 14 пт, межстрочный 1,5, по ширине, абзацный отступ 1,25 см, интервалы 0 (4.1.5).',
    'Номера страниц - внизу по центру; титул, задание и лист замечаний считаются, но без номера (4.3).',
    'Пустые абзацы запрещены - редактор их просто не выводит в DOCX.',
  ], sample: <p className="gs-p">Основной текст отчёта начинается с абзацного отступа 1,25 см и выравнивается по ширине.</p> },
  { id: 'heads', title: 'Заголовки', icon: 'h1', rules: [
    'Раздел «1 Название», подраздел «1.1 Название», пункт «1.1.1» - номер без точки, в конце точки нет (4.2).',
    'Полужирные, с абзацного отступа; раздел - с новой страницы.',
    'Структурные элементы («Введение», «Выводы», «Заключение», «Список использованных источников») - по центру, без номера.',
    'Номера в редакторе ставятся сами - вручную их не пишите.',
  ], sample: <><div className="gs-h">1 Исследование конфигурации узла</div><div className="gs-h">1.1 Параметры сетевого адаптера</div><div className="gs-struct">Выводы</div></>,
  action: [['block.h1', 'Раздел'], ['block.h2', 'Подраздел']] },
  { id: 'lists', title: 'Перечисления', icon: 'list', rules: [
    'После фразы с двоеточием; позиции с дефиса «-», со строчной буквы, в конце «;», в последней «.» (4.4).',
    'Если на позицию ссылаются - «а)», «б)» (без ё, з, й, о, ч, ъ, ы, ь); вложенные - «1)», «2)».',
    '«1.» с точкой для перечислений не допускается.',
  ], sample: <><p className="gs-p">Для работы использованы утилиты:</p><p className="gs-p">- ping;</p><p className="gs-p">- tracert.</p></>,
  action: [['list.dash', 'Перечисление «–»'], ['list.letter', 'Перечисление «а)»']] },
  { id: 'figs', title: 'Рисунки', icon: 'image', rules: [
    'Все изображения (скриншоты, схемы, графики) - «Рисунок»; нумерация сквозная (4.5).',
    'Подпись под рисунком по центру: «Рисунок 3. Название» - 12 пт, в конце точки нет.',
    'Рисунок - сразу после абзаца с первой ссылкой «…на рисунке 3»; ссылка обязательна и стоит до рисунка.',
    'Картинку можно вставить Ctrl+V или перетащить файл - подпись и номер появятся сами.',
  ], sample: <><div className="gs-fig" /><div className="gs-cap">Рисунок 3. Результат выполнения команды ipconfig</div></>,
  action: [['insert.figure', 'Вставить рисунок'], ['refs.xref', 'Ссылка на рисунок']] },
  { id: 'tables', title: 'Таблицы', icon: 'table', rules: [
    'Название над таблицей слева без отступа: «Таблица 1. Название» (4.9).',
    'Текст таблицы 12 пт, одинарный; головка обязательна, её текст по центру.',
    'Графа «№ п/п» запрещена; ссылка «в таблице 1» - до таблицы.',
    'При переносе на другую страницу сборщик сам пишет «Продолжение таблицы 1».',
  ], sample: <><div className="gs-tcap">Таблица 1. Параметры конфигурации</div><table className="gs-table"><tbody><tr><th>Параметр</th><th>Значение</th></tr><tr><td>IP-адрес</td><td>192.168.1.37</td></tr></tbody></table></>,
  action: [['insert.table', 'Вставить таблицу']] },
  { id: 'eqs', title: 'Формулы', icon: 'formula', rules: [
    'Формула - отдельной строкой, номер в скобках у правого поля: (1); сквозная нумерация (4.6).',
    'Пояснение символов - сразу после формулы: «где t – время, с;».',
    'Ссылка на формулу: «по формуле (1)».',
  ], sample: <div className="gs-eq"><span><i>t̄</i> = (1/<i>n</i>) Σ <i>t<sub>i</sub></i></span><span>(1)</span></div>,
  action: [['math.formula', 'Вставить формулу']] },
  { id: 'refs', title: 'Ссылки и источники', icon: 'cite', rules: [
    'Ссылка на источник - номер в квадратных скобках: [3], со страницей: [3, с. 25] (4.13).',
    'Список - по ГОСТ Р 7.0.100, по алфавиту (иностранные - в конце); номера ставятся сами.',
    'На каждый источник должна быть ссылка в тексте.',
  ], sample: <p className="gs-p">Протокол ICMP описан в спецификации [1, с. 4].</p>,
  action: [['refs.cite', 'Ссылка [N]'], ['refs.addSource', 'Новый источник']] },
  { id: 'apps', title: 'Приложения', icon: 'attach', rules: [
    'В конце отчёта; «Приложение А» по центру, под ним статус «(обязательное)», затем название (4.16).',
    'Буквы без Ё, З, Й, О, Ч, Ъ, Ы, Ь; рисунки и таблицы внутри - «Рисунок А.1».',
    'На каждое приложение должна быть ссылка в тексте.',
  ], action: [['sto.appendix', 'Добавить приложение']] },
  { id: 'typo', title: 'Типографика', icon: 'type', rules: [
    'Тире «–» с пробелами; в диапазонах «1–4» без пробелов; дефис «-» без пробелов.',
    'Кавычки «ёлочки»; в коде и командах - прямые.',
    USE_NBSP ? 'Неразрывный пробел: «64 байта», «рисунке 5», «т. е.»; двойные пробелы недопустимы.'
      : '«64 байта», «рисунке 5», «т. е.», «№ 5», «50 %» - через обычный пробел (неразрывные при экспорте заменяются обычными); двойные пробелы недопустимы.',
    'Непечатаемые знаки (Ctrl+Shift+8) покажут лишние пробелы.',
  ], action: [['view.marks', 'Показать знаки ¶']] },
  { id: 'code', title: 'Программный код', icon: 'braces', rules: [
    'Листинг с названием над ним: «Листинг 1. Название»; Courier New 10 пт в рамке (6.1).',
    'Длинный листинг переносится на следующую страницу по строкам: сборщик сам делит его и пишет над продолжением «Продолжение листинга 1», над последней частью - «Окончание листинга 1» (как у таблиц); на листах редактора это видно сразу.',
    'Короткие команды в тексте - моноширинным шрифтом (Ctrl+E).',
  ], action: [['insert.listing', 'Вставить листинг']] },
  { id: 'struct', title: 'Структура отчёта', icon: 'list-tree', rules: [
    'Титульный лист → (Задание) → Лист для замечаний → Оглавление → (Введение) → разделы → Выводы/Заключение → (Список источников) → (Приложения).',
    'Титул, оглавление и задание собираются сами из «СТО → Титул и данные».',
    'Проверка всех требований - F7 («Проверить по СТО»).',
  ], action: [['sto.meta', 'Титул и данные'], ['sto.check', 'Проверить по СТО']] },
];

export function StoGuideDialog({ close, props }: DialogProps) {
  const [cur, setCur] = useState<string>(props?.topic || 'write');
  const t = TOPICS.find((x) => x.id === cur) || TOPICS[0];
  const hasProject = !!appStore.getState().projectId;
  return (
    <Modal title="Памятка по СТО САФУ 2026" onClose={() => close()} width={920} testid="guide-dialog" className="guide-modal">
      <div className="guide">
        <nav className="guide-nav" aria-label="Темы">
          {TOPICS.map((x) => (
            <button key={x.id} type="button" className={'guide-tab' + (x.id === cur ? ' is-active' : '')} onClick={() => setCur(x.id)}
              data-testid={'guide-' + x.id}>
              <Icon name={x.icon} size={16} /><span>{x.title}</span>
            </button>
          ))}
        </nav>
        <article className="guide-body">
          <h2>{t.title}</h2>
          <ul className="guide-rules">{t.rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
          {t.sample && <div className="guide-sample"><div className="guide-sample-label">Как выглядит</div><div className="guide-paper">{t.sample}</div></div>}
          {hasProject && t.action && (
            <div className="guide-actions">
              {t.action.filter(([id]) => registry.commands.has(id)).map(([id, label]) => (
                <Btn key={id} icon={registry.commands.get(id)?.icon} label={label} onClick={() => { close(); runCommand(id); }} />
              ))}
            </div>
          )}
        </article>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ знакомство
const TOUR = [
  { icon: 'sparkles', title: 'Пишите - оформит СТО', text: 'Шрифт, поля, отступы, номера разделов, рисунков, таблиц и формул ставятся сами. Выбирайте только стиль: «Раздел», «Текст», «Перечисление» (Ctrl+Alt+1…3, Ctrl+Alt+0).' },
  { icon: 'paste', title: 'Вставка без мучений', text: 'Ctrl+V вставляет из Word, Excel и браузера; скриншоты и файлы картинок - сразу рисунком с подписью. «/» в начале абзаца - меню вставки с поиском.' },
  { icon: 'check', title: 'Проверка и экспорт', text: 'F7 проверяет отчёт по всем требованиям СТО и показывает, где ошибка. DOCX и PDF - кнопками вверху; «Файл → Версии» хранит историю.' },
  { icon: 'command', title: 'Любая команда - Ctrl+K', text: 'Наберите «рисунок», «pdf», «выводы» - и выполните или перейдите. F1 - памятка по СТО и горячие клавиши.' },
];

const tourListeners = new Set<() => void>();
/** Показать знакомство снова (команда «Знакомство с редактором»). */
export function showTour() { writePref('tour.done', '0'); writePref('tour.force', '1'); tourListeners.forEach((f) => f()); }

export function TourCard() {
  const pid = useApp((s) => s.projectId);
  const [step, setStep] = useState(0);
  const [hidden, setHidden] = useState(() => readPref('tour.done', '') === '1'
    || ((navigator as any).webdriver && readPref('tour.force', '') !== '1'));
  useEffect(() => {
    const f = () => { setStep(0); setHidden(false); };
    tourListeners.add(f);
    return () => { tourListeners.delete(f); };
  }, []);
  if (hidden || !pid) return null;
  const done = () => { writePref('tour.done', '1'); setHidden(true); };
  const s = TOUR[step];
  return (
    <div className="tour-card" role="dialog" aria-label="Знакомство с редактором" data-testid="tour-card">
      <div className="tour-head"><Icon name={s.icon} size={20} /><span>{s.title}</span>
        <button type="button" className="tour-x" aria-label="Закрыть" onClick={done}><Icon name="close" size={14} /></button></div>
      <div className="tour-text">{s.text}</div>
      <div className="tour-foot">
        <span className="tour-dots">{TOUR.map((_, i) => <span key={i} className={i === step ? 'is-on' : ''} />)}</span>
        <span className="topbar-spacer" />
        {step < TOUR.length - 1
          ? <><button type="button" className="link-btn" onClick={done}>Пропустить</button><Btn label="Далее" primary onClick={() => setStep(step + 1)} /></>
          : <Btn label="Начать" primary onClick={done} testid="tour-done" />}
      </div>
    </div>
  );
}

export function setupHelp(r: RegistryApi) {
  r.commands.add({ id: 'app.palette', title: 'Командная палитра', description: 'Найти любую команду или раздел документа', icon: 'command',
    shortcut: 'Mod-k', run: () => openPalette() });
  r.commands.add({ id: 'app.help', title: 'Памятка по СТО', description: 'Главные правила оформления с образцами', icon: 'sto-book', shortcut: 'F1',
    run: (c) => c.openDialog('shell.guide') });
  r.commands.add({ id: 'app.shortcuts', title: 'Горячие клавиши', icon: 'keyboard', shortcut: 'Mod-/', run: (c) => c.openDialog('shell.shortcuts') });
  r.commands.add({ id: 'app.tour', title: 'Знакомство с редактором', description: 'Показать подсказки первого запуска', icon: 'tip',
    run: () => showTour() });
  r.dialogs.add({ id: 'shell.shortcuts', component: ShortcutsDialog });
  r.dialogs.add({ id: 'shell.guide', component: StoGuideDialog });

  r.ribbonTabs.add({ id: 'help', title: 'Справка', order: 70 });
  r.ribbonGroups.add({ id: 'help.main', tab: 'help', title: 'Справка', order: 10 });
  r.ribbonItems.add({ id: 'help-guide', group: 'help.main', type: 'large', command: 'app.help', label: 'Памятка СТО' });
  r.ribbonItems.add({ id: 'help-keys', group: 'help.main', type: 'large', command: 'app.shortcuts', label: 'Клавиши' });
  r.ribbonItems.add({ id: 'help-palette', group: 'help.main', type: 'large', command: 'app.palette', label: 'Все команды' });
  r.ribbonItems.add({ id: 'help-tour', group: 'help.main', type: 'large', command: 'app.tour', label: 'Знакомство' });
}
